import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  OperatorFunction,
  catchError,
  combineLatest,
  map,
  of,
  scan,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { AuthService } from '../../core/auth/auth.service';
import { NotificationsService } from '../../core/services/notifications.service';
import { CustomerProfilesService } from '../../features/customer-profiles/customer-profiles.service';
import { ProjectsService } from '../../features/projects/projects.service';
import { ErrorService } from '../../core/services/error.service';
import {
  INotificationFeedItem,
  NotificationItemType,
  NotificationTypeFilter,
} from '../../core/types/notification.interface';
import { IMeta } from '../../core/types/http.interface';
import { IProjectList } from '../../core/types/project.interface';
import { DashboardPagerComponent } from './dashboard-pager/dashboard-pager.component';
import { DashboardStatCardComponent } from './dashboard-stat-card/dashboard-stat-card.component';
import {
  DashboardRequestKind,
  DashboardRequestRow,
  DashboardRequestRowComponent,
} from './dashboard-request-row/dashboard-request-row.component';

interface DashboardFilter {
  value: NotificationTypeFilter;
  label: string;
}

interface RecentProjectRow {
  id: string;
  name: string;
  subtitle: string;
  timestamp: string;
  link: string[];
}

interface RequestFeedQuery {
  filter: NotificationTypeFilter;
  page: number;
  limit: number;
}

interface RequestFeedState {
  rows: DashboardRequestRow[];
  /** Null until the first page for the dashboard has loaded. */
  meta: IMeta | null;
  loading: boolean;
}

interface DashboardViewModel {
  openRequests: number | null;
  pendingApprovals: number | null;
  profilesToReview: number | null;
  pricingRequests: number | null;
  newProjects: number | null;
  recentProjects: RecentProjectRow[];
  feed: RequestFeedState;
  query: RequestFeedQuery;
}

/** What the signed-in admin-org user can act on; drives which cards, pills and links render. */
interface DashboardAccess {
  fullAdminOrg: boolean;
  estimateSettings: boolean;
  customerProfiles: boolean;
  batteryCatalog: boolean;
  poSalesApproval: boolean;
  poSupportApproval: boolean;
}

const FEED_PAGE_SIZE = 10;
const RECENT_PROJECTS_LIMIT = 4;

@Component({
  selector: 'admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    RouterLink,
    DashboardPagerComponent,
    DashboardStatCardComponent,
    DashboardRequestRowComponent,
  ],
})
export class AdminDashboardComponent {
  private readonly authService = inject(AuthService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly customerProfilesService = inject(CustomerProfilesService);
  private readonly projectsService = inject(ProjectsService);
  private readonly errorService = inject(ErrorService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly access: DashboardAccess = {
    fullAdminOrg: this.authService.can('fullAdmin', 'user'),
    estimateSettings: this.authService.can('manageEstimateSettings'),
    customerProfiles: this.authService.can('customerProfilesAdmin', 'user'),
    batteryCatalog: this.authService.can('batteryCatalog', 'user'),
    poSalesApproval: this.authService.can('approveSales', 'user'),
    poSupportApproval: this.authService.can('approveSupport', 'user'),
  };

  readonly canApprovePos = this.access.poSalesApproval || this.access.poSupportApproval;

  /** Open requests + New projects always render; the rest depend on access. */
  readonly statCardCount = 2
    + Number(this.canApprovePos)
    + Number(this.access.customerProfiles)
    + Number(this.access.fullAdminOrg);

  readonly filters: DashboardFilter[] = this.buildFilters();

  readonly feedLimitOptions: readonly number[] = [10, 15, 30, 50];

  /** Seeded from `?queue=&page=&limit=` so Back from a reviewed request returns to the same page. */
  private readonly query$ = new BehaviorSubject<RequestFeedQuery>(this.initialQuery());

  private readonly openRequests$ = this.notificationsService.activeRequestCount$.pipe(
    startWith(null as number | null),
  );

  /** Sales, support, or (superadmin) both PO approval queues — scoped server-side by role. */
  private readonly pendingApprovals$ = this.canApprovePos
    ? this.countNotifications('po_review_requests')
    : of(null);

  private readonly profilesToReview$ = this.access.customerProfiles
    ? this.customerProfilesService.getAdminQueue({ limit: 1 }).pipe(
        map((response) => response.meta.total),
        take(1),
        this.fallbackTo<number | null>(null),
        startWith(null as number | null),
      )
    : of(null);

  private readonly pricingRequests$ = this.access.fullAdminOrg
    ? this.countNotifications('special_pricing_requests')
    : of(null);

  private readonly newProjects$ = this.projectsService
    .findMany({ sort: '-created', limit: 1 })
    .pipe(
      map((response) => response.meta.total),
      take(1),
      this.fallbackTo<number | null>(null),
      startWith(null as number | null),
    );

  private readonly recentProjects$ = this.projectsService
    .findMany({ sort: '-created', limit: RECENT_PROJECTS_LIMIT })
    .pipe(
      map((response) => response.items.map((project) => this.toRecentProject(project))),
      take(1),
      this.fallbackTo<RecentProjectRow[]>([]),
      startWith([] as RecentProjectRow[]),
    );

  /**
   * The current page of the selected queue. While the next page loads the previous rows stay
   * on screen (dimmed) instead of flashing the empty state.
   */
  private readonly feed$: Observable<RequestFeedState> = this.query$.pipe(
    switchMap((query) =>
      this.notificationsService
        .getNotifications({ notification_type: query.filter, page: query.page, limit: query.limit })
        .pipe(
          map((response): RequestFeedState => ({
            rows: response.items.map((item) => this.toRequestRow(item)),
            meta: response.meta,
            loading: false,
          })),
          take(1),
          tap((state) => this.stepBackPastLastPage(query, state.meta)),
          this.fallbackTo<RequestFeedState>({
            rows: [],
            meta: { total: 0, limit: query.limit, current: 1 },
            loading: false,
          }),
          startWith(null),
        ),
    ),
    scan(
      (previous: RequestFeedState, next: RequestFeedState | null): RequestFeedState =>
        next ?? { ...previous, loading: true },
      { rows: [], meta: null, loading: true },
    ),
  );

  readonly vm$: Observable<DashboardViewModel> = combineLatest({
    openRequests: this.openRequests$,
    pendingApprovals: this.pendingApprovals$,
    profilesToReview: this.profilesToReview$,
    pricingRequests: this.pricingRequests$,
    newProjects: this.newProjects$,
    recentProjects: this.recentProjects$,
    feed: this.feed$,
    query: this.query$,
  });

  constructor() {
    this.notificationsService.refreshActiveRequestCount();
  }

  onSelectFilter(filter: NotificationTypeFilter): void {
    const query = this.query$.getValue();
    if (query.filter !== filter) {
      this.updateQuery({ ...query, filter, page: 1 });
    }
  }

  onPageChange(page: number): void {
    this.updateQuery({ ...this.query$.getValue(), page });
  }

  onLimitChange(limit: number): void {
    this.updateQuery({ ...this.query$.getValue(), limit, page: 1 });
  }

  emptyMessage(filter: NotificationTypeFilter): string {
    switch (filter) {
      case 'po_sales_review_requests':
        return 'No projects are waiting for sales approval.';
      case 'po_support_review_requests':
        return 'No projects are waiting for support approval.';
      default:
        return 'No open requests in this view.';
    }
  }

  onOpenRequest(row: DashboardRequestRow): void {
    const target = row.targetLink.trim();
    if (target.startsWith('/') && !target.startsWith('//')) {
      void this.router.navigateByUrl(target);
    }
  }

  private initialQuery(): RequestFeedQuery {
    const params = this.route.snapshot.queryParamMap;
    const queue = params.get('queue');
    const page = Number(params.get('page'));
    const limit = Number(params.get('limit'));
    return {
      filter: this.filters.find((filter) => filter.value === queue)?.value ?? 'open_requests',
      page: Number.isInteger(page) && page > 1 ? page : 1,
      limit: this.feedLimitOptions.includes(limit) ? limit : FEED_PAGE_SIZE,
    };
  }

  /** Defaults are dropped from the URL; replaceUrl keeps paging out of the history stack. */
  private updateQuery(query: RequestFeedQuery): void {
    this.query$.next(query);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        queue: query.filter === 'open_requests' ? null : query.filter,
        page: query.page > 1 ? query.page : null,
        limit: query.limit === FEED_PAGE_SIZE ? null : query.limit,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** A queue can shrink between visits (requests get resolved); land on its last page, not an empty one. */
  private stepBackPastLastPage(query: RequestFeedQuery, meta: IMeta | null): void {
    if (meta == null) {
      return;
    }
    const lastPage = Math.max(1, Math.ceil(meta.total / Math.max(1, query.limit)));
    if (query.page > lastPage) {
      this.updateQuery({ ...query, page: lastPage });
    }
  }

  private buildFilters(): DashboardFilter[] {
    const filters: DashboardFilter[] = [{ value: 'open_requests', label: 'All' }];
    if (this.access.poSalesApproval) {
      filters.push({ value: 'po_sales_review_requests', label: 'Sales approval' });
    }
    if (this.access.poSupportApproval) {
      filters.push({ value: 'po_support_review_requests', label: 'Support approval' });
    }
    if (this.access.fullAdminOrg) {
      filters.push({ value: 'special_pricing_requests', label: 'Pricing' });
    }
    if (this.access.customerProfiles) {
      filters.push({ value: 'customer_profile_requests', label: 'Profiles' });
    }
    if (this.access.batteryCatalog) {
      filters.push({ value: 'battery_model_requests', label: 'Batteries' });
    }
    return filters;
  }

  private countNotifications(notificationType: NotificationTypeFilter): Observable<number | null> {
    return this.notificationsService.getNotifications({ notification_type: notificationType, limit: 1 }).pipe(
      map((response) => response.meta.total),
      take(1),
      this.fallbackTo<number | null>(null),
      startWith(null as number | null),
    );
  }

  /** Toast the error via ErrorService, then keep the dashboard alive with a fallback value. */
  private fallbackTo<T>(fallback: T): OperatorFunction<T, T> {
    return catchError((error: unknown) => {
      this.errorService.handle(error);
      return of(fallback);
    });
  }

  private toRequestRow(item: INotificationFeedItem): DashboardRequestRow {
    const kind = this.requestKind(item.type);
    return {
      // A project awaiting both approvals appears once per queue, so key on type + id.
      id: `${item.type}:${item.id}`,
      kind,
      category: this.categoryLabel(item.type, kind),
      title: item.title || 'Untitled request',
      subtitle: item.subtitle ?? '',
      timestamp: this.formatRelative(item.created),
      actionLabel: this.actionLabel(kind),
      actionVariant: kind === 'message' || kind === 'project' ? 'ghost' : 'primary',
      targetLink: item.target_link,
    };
  }

  private toRecentProject(project: IProjectList): RecentProjectRow {
    const meta = [project.created_by, project.reseller].filter(Boolean).join(' · ');
    return {
      id: project._id,
      name: project.name,
      subtitle: meta,
      timestamp: this.formatCompact(project.created),
      link: ['/admin', 'projects', project._id],
    };
  }

  private requestKind(type: NotificationItemType | string): DashboardRequestKind {
    switch (type) {
      case 'special_pricing_request':
        return 'pricing';
      case 'customer_profile_request':
        return 'profile';
      case 'battery_model_request':
        return 'battery';
      case 'po_sales_review_request':
      case 'po_support_review_request':
        return 'po';
      case 'unread_message':
        return 'message';
      default:
        return 'project';
    }
  }

  private categoryLabel(type: NotificationItemType | string, kind: DashboardRequestKind): string {
    if (type === 'po_sales_review_request') {
      return 'Sales approval';
    }
    if (type === 'po_support_review_request') {
      return 'Support approval';
    }
    switch (kind) {
      case 'pricing':
        return 'Pricing request';
      case 'profile':
        return 'Profile review';
      case 'battery':
        return 'Battery model';
      case 'po':
        return 'PO review';
      case 'message':
        return 'Message';
      default:
        return 'New project';
    }
  }

  private actionLabel(kind: DashboardRequestKind): string {
    switch (kind) {
      case 'pricing':
        return 'Review pricing';
      case 'message':
        return 'Open project';
      case 'project':
        return 'View project';
      default:
        return 'Review';
    }
  }

  private formatRelative(iso: string | null): string {
    const minutes = this.minutesSince(iso);
    if (minutes == null) {
      return '';
    }
    if (minutes < 1) {
      return 'Just now';
    }
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    if (days === 1) {
      return 'Yesterday';
    }
    if (days < 7) {
      return `${days}d ago`;
    }
    return new Date(iso as string).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  private formatCompact(iso: string | null): string {
    const minutes = this.minutesSince(iso);
    if (minutes == null) {
      return '';
    }
    if (minutes < 1) {
      return 'now';
    }
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days}d`;
    }
    const weeks = Math.floor(days / 7);
    if (weeks < 5) {
      return `${weeks}w`;
    }
    return `${Math.floor(days / 30)}mo`;
  }

  private minutesSince(iso: string | null): number | null {
    if (!iso) {
      return null;
    }
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) {
      return null;
    }
    return Math.floor((Date.now() - then) / 60000);
  }
}
